import { supabase } from "@/integrations/supabase/client";

export interface CollisionRow {
  filename: string;
  existing_document_id: number;
  current_version: number;
  version_group: string | null;
  /** SHA256 hash of the existing document's bytes. `null` for legacy rows
   *  uploaded before content_hash was tracked. */
  existing_content_hash: string | null;
  /** Byte size of the existing document (from storage). `null` if unknown. */
  existing_size: number | null;
  /** When the previous version was uploaded (ISO-8601). Uses `created_at`
   *  — `modified_at` drifts with pipeline state transitions and the RPC's
   *  `is_latest_version` flip, so it misreads as "just now" the moment a
   *  new version is being uploaded. */
  existing_modified_at: string | null;
  /** Display name ("First Last") of the uploader of the existing row. */
  existing_uploader_name: string | null;
}

export interface CollisionCheckParams {
  projectId: number;
  /** null = root of the project (no folder). */
  folderId: number | null;
  /** Filenames to check. Deduplicated internally. */
  filenames: string[];
}

/**
 * Look up existing active (not soft-deleted, non-container-child) documents
 * that would collide by filename inside a single `(project_id, folder_id)`
 * bucket. Returns one row per existing document for each matching filename.
 *
 * The `filter` matches the identity used by `create_document_with_versioning`:
 * `(project_id, folder_id, parent_document_id=NULL, filename)`.
 */
export async function checkUploadCollisions({
  projectId,
  folderId,
  filenames,
}: CollisionCheckParams): Promise<CollisionRow[]> {
  const unique = Array.from(new Set(filenames.map((n) => n.trim()).filter((n) => n.length > 0)));
  if (unique.length === 0) return [];

  let query = supabase
    .from("documents")
    .select(
      `
        id,
        filename,
        version,
        version_group,
        content_hash,
        size,
        created_at,
        user_profiles!documents_created_by_fk ( first_name, last_name )
      `
    )
    .eq("project_id", projectId)
    .in("filename", unique)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .is("parent_document_id", null);

  query = folderId === null ? query.is("folder_id", null) : query.eq("folder_id", folderId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return (data ?? []).map((row) => {
    const profile = (row as { user_profiles?: { first_name?: string; last_name?: string } })
      .user_profiles;
    const uploaderName = profile
      ? [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim() || null
      : null;
    return {
      filename: row.filename,
      existing_document_id: row.id,
      current_version: row.version ?? 1,
      version_group: row.version_group ?? null,
      existing_content_hash: row.content_hash ?? null,
      existing_size: row.size ?? null,
      existing_modified_at: row.created_at ?? null,
      existing_uploader_name: uploaderName,
    };
  });
}

/**
 * Fetch every active (not soft-deleted) filename in a folder — used to
 * compute the next free "(N)" suffix for duplicate uploads. Includes versioned
 * rows even when they aren't `is_latest_version=true` so we don't reuse a
 * suffix that's already present in version history.
 */
export async function fetchFilenamesInFolder({
  projectId,
  folderId,
}: {
  projectId: number;
  folderId: number | null;
}): Promise<string[]> {
  let query = supabase
    .from("documents")
    .select("filename")
    .eq("project_id", projectId)
    .eq("is_deleted", false)
    .is("parent_document_id", null);

  query = folderId === null ? query.is("folder_id", null) : query.eq("folder_id", folderId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return (data ?? []).map((r: { filename: string }) => r.filename);
}
